import { useCallback, useMemo, useReducer } from 'react';
import { WHATSAPP_MESSAGE_SOURCE, variableChoicesFor } from '../../utils/whatsapp';
import {
  LAST_STEP,
  audienceKey,
  audienceProblem,
  campaignBody,
  campaignFingerprint,
  defaultCampaignName,
  initialState,
  mappingComplete,
  previewBody,
  wizardReducer,
} from './campaignWizardState';
import useAudiencePreview from './useAudiencePreview';
import useCampaignDraft from './useCampaignDraft';
import useWizardLookups from './useWizardLookups';

/** Why the last step cannot send right now, or '' when sending is on. */
function sendingBlock(status) {
  if (status.loading || (!status.data && !status.error)) return 'Checking whether sending is switched on...';
  if (status.error) return 'Could not check whether sending is switched on, so nothing can be sent.';
  if (!status.data.configured) return 'WhatsApp is not configured on this environment, so nothing can be sent.';
  if (!status.data.send_enabled) return 'Sending is switched off on this environment; nothing can be sent.';
  return '';
}

/**
 * All state and side effects of the new-campaign wizard, so the step components only render.
 * @param {{onSent?: (campaignId: string) => void}} args
 */
export default function useCampaignWizard({ onSent } = {}) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const lookups = useWizardLookups({
    needsEvents: state.source === WHATSAPP_MESSAGE_SOURCE.WALK_IN_LEAD,
    needsTemplates: state.step >= 2,
  });

  const template = useMemo(
    () => (lookups.templates.data || []).find((t) => t.id === state.templateId) || null,
    [lookups.templates.data, state.templateId],
  );
  const key = audienceKey(state);
  const preview = useAudiencePreview({ active: state.step === 1, body: previewBody(state), audienceKey: key });
  const draft = useCampaignDraft({
    body: campaignBody(state, template),
    fingerprint: campaignFingerprint(state, template),
    onSent,
  });

  const patch = useCallback((changes) => dispatch({ type: 'patch', patch: changes }), []);
  const sendBlock = sendingBlock(lookups.status);
  const sendable = preview.fresh ? preview.data.sendable_count : 0;

  /** What still stops Continue on the current step ('' when nothing does). */
  let problem = '';
  if (state.step === 0) problem = audienceProblem(state);
  else if (state.step === 1) {
    if (preview.error) problem = 'The list could not be loaded';
    else if (!preview.fresh) problem = 'Updating the list...';
    else if (sendable === 0) problem = 'Nobody can be messaged with these choices. Go back and change the audience.';
  } else if (state.step === 2) {
    if (!template) problem = 'Choose a template';
    else if (!mappingComplete(state, template)) problem = 'Choose a value for every placeholder';
  }

  const go = (step) =>
    dispatch({ type: 'go', step, defaultName: template ? defaultCampaignName(template.name) : '' });

  return {
    state,
    patch,
    dispatch,
    lookups,
    template,
    preview,
    draft,
    problem,
    sendBlock,
    onSent,
    choices: variableChoicesFor(state.source),
    canContinue: state.step < LAST_STEP && !problem,
    next: () => !problem && state.step < LAST_STEP && go(state.step + 1),
    back: () => state.step > 0 && !draft.testing && go(state.step - 1),
  };
}
